import { Box, DialogContent, Icon, Stack, Typography } from '@mui/material'
import PropTypes from 'prop-types'
import { forwardRef, useCallback, useEffect, useRef } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'

import cameraIcon from '@hapana/shared/web/styles/assets/svgs/camera-large.svg'
import cameraSlashIcon from '@hapana/shared/web/styles/assets/svgs/camera-slash-large.svg'
import ButtonSelectFile from '../../../components/shared/button-select-file/button-select-file.component'
import DialogLayout from '../../../components/shared/dialog/dialog-layout/dialog-layout.component'
import {
  CLOSE_TYPES,
  SIZES,
} from '../../../components/shared/dialog/dialog-layout/dialog-layout.constants'
import { useDialog } from '../../../components/shared/dialog/dialog.hooks'
import { useIsCameraEnabled } from '../../../hooks/useIsCameraEnabled.hooks'
import { filterAllowedFileFormats } from '@hapana/shared/utils/camera.util'
import { PHOTO_CLIENT_RULES } from '@hapana/shared/constants/rules'

import { MEGABYTES_IN_BYTES } from '@hapana/shared/constants/rules'

import ModalCameraFraming from './modal-camera-framing.component'
import ModalCropClientPhoto from './modal-crop-photo.component'
import { ALLOWED_TYPES, INPUT_FILE_NAME, MAX_IMAGE_SIZE_MB } from './modal-upload-photo.constants'
import { styles } from './modal-upload-photo.styles'

// Utility function to handle the actual upload - can be called from crop modal
export const uploadImageFile = async (file, mutation) => {
  const fileExtension = file.name.split('.').pop()?.toLowerCase() || 'jpg'
  const fileSize = file.size

  // Get the correct Content-Type that matches the backend logic
  const getContentType = (extension) => {
    const contentTypeMap = {
      jpg: 'image/jpeg',
      jpeg: 'image/jpeg',
      png: 'image/png',
      gif: 'image/gif',
      webp: 'image/webp',
      bmp: 'image/bmp',
      tiff: 'image/tiff',
    }
    return contentTypeMap[extension] || 'image/jpeg'
  }

  return new Promise((resolve, reject) => {
    mutation.mutate(
      { fileExtension, fileSize, file },
      {
        onSuccess: async (uploadResponse) => {
          try {
            const contentType = getContentType(fileExtension)

            // Upload the file to the presigned URL
            const uploadResult = await fetch(uploadResponse.uploadUrl, {
              method: 'PUT',
              body: file,
              headers: {
                'Content-Type': contentType,
              },
            })

            if (uploadResult.ok) {
              resolve(uploadResponse)
            } else {
              reject(new Error(`Upload failed: ${uploadResult.statusText}`))
            }
          } catch (error) {
            reject(error)
          }
        },
        onError: (error) => {
          reject(error)
        },
      }
    )
  })
}

const ModalUploadPhoto = forwardRef(
  (
    {
      hasCam,
      mutation,
      directOpen,
      allowedTypes,
      isBannerImage,
      imageDelayedToLoad,
      maxImageSize,
      usePopDialog,
      cropTitle,
    },
    ref
  ) => {
    const { pushDialog, filterDialogStack, dialogState, popDialog } = useDialog()
    const isCameraEnabled = useIsCameraEnabled()
    const { t } = useTranslation('lib')
    const formMethods = useForm({
      mode: 'onChange',
    })
    const inputRef = useRef(null)
    const allowedFileFormatTypes = allowedTypes || ALLOWED_TYPES
    const { watch, formState, handleSubmit } = formMethods
    const { errors, isValid } = formState

    const hasErrors = Object.keys(errors).length > 0
    const imageData = watch(INPUT_FILE_NAME)

    const openFile = () => {
      inputRef.current.click()
    }

    const onSubmit = useCallback(
      (data) => {
        const [file] = data
        if (file) {
          const isMaxSizeExceeded = file?.size > MAX_IMAGE_SIZE_MB * MEGABYTES_IN_BYTES

          if (isMaxSizeExceeded) {
            // Handle file size error - you might want to show a toast or error message
            console.error('File size exceeds maximum allowed size')
            return
          }

          // Just read the file for cropping - no upload yet
          const reader = new FileReader()
          reader.readAsDataURL(file)
          reader.onload = () => {
            pushDialog(ModalCropClientPhoto, {
              image: reader.result,
              originalFile: file, // Pass the original file for later upload
              hasCam,
              mutation,
              directOpen,
              isBannerImage,
              imageDelayedToLoad,
              maxImageSize,
              usePopDialog,
              title: cropTitle,
            })
          }
        }
      },
      [
        pushDialog,
        hasCam,
        mutation,
        directOpen,
        isBannerImage,
        imageDelayedToLoad,
        maxImageSize,
        usePopDialog,
      ]
    )

    const uploadPhotoAction = {
      label: t('lib:profilePicture.uploadPhoto'),
      action: openFile,
    }
    const useCameraAction = {
      label: t('lib:profilePicture.useCamera'),
      action: () =>
        pushDialog(ModalCameraFraming, {
          isCameraEnabled,
          mutation,
          isBannerImage,
        }),
    }

    const dialogLayoutProps = {
      withCam: {
        primaryAction: useCameraAction,
        secondaryAction: uploadPhotoAction,
      },
      withoutCam: { primaryAction: uploadPhotoAction },
    }

    useEffect(() => {
      if (isValid) {
        handleSubmit(onSubmit(imageData))
      }
    }, [isValid, handleSubmit, imageData, onSubmit])

    if (directOpen) {
      return (
        <FormProvider {...formMethods}>
          <form>
            <Stack sx={styles.inputFileWarning}>
              <ButtonSelectFile
                labelProps={{ ref }}
                aria-label={t('lib:profilePicture.uploadPhoto')}
                accept={allowedFileFormatTypes.join(',')}
                rules={PHOTO_CLIENT_RULES(allowedFileFormatTypes)}
                hiddenButton
                name={INPUT_FILE_NAME}
                test-id={INPUT_FILE_NAME}
                directOpen
              />
            </Stack>
          </form>
        </FormProvider>
      )
    }
    return (
      <DialogLayout
        {...(hasCam ? dialogLayoutProps.withCam : dialogLayoutProps.withoutCam)}
        headerStylesOverride={styles.dialogHeader}
        closeType={CLOSE_TYPES.ICON}
        size={SIZES.XS}
        closeAction={() => {
          if (dialogState?.dialogFilterParams) {
            filterDialogStack(
              dialogState?.dialogFilterParams?.key,
              dialogState?.dialogFilterParams?.value
            )
          } else {
            popDialog()
          }
        }}
        hasMobileBottomSheet
      >
        <DialogContent sx={styles.dialogContent}>
          <FormProvider {...formMethods}>
            <form>
              <Box sx={[styles.titleWrapper, hasErrors && styles.titleWrapperInputError]}>
                <Icon sx={styles.cameraIcon} component={hasCam ? cameraIcon : cameraSlashIcon} />
                <Typography typography="header1" color="gray.80">
                  {hasCam
                    ? t('lib:profilePicture.changePhoto')
                    : t('lib:profilePicture.cameraNotFound')}
                </Typography>
                <Typography color="gray.56" typography="header3">
                  {hasCam
                    ? t('lib:profilePicture.takePictureOrUpload')
                    : t('lib:profilePicture.cantConnectCamera')}
                </Typography>
              </Box>
              <Stack sx={styles.inputFileWarning}>
                <ButtonSelectFile
                  labelProps={{ ref: inputRef }}
                  aria-label={t('lib:profilePicture.uploadPhoto')}
                  accept={allowedFileFormatTypes.join(',')}
                  rules={PHOTO_CLIENT_RULES(allowedFileFormatTypes)}
                  hiddenButton
                  name={INPUT_FILE_NAME}
                  test-id={INPUT_FILE_NAME}
                />
                <Typography color="gray.56" typography="large">
                  <Trans
                    i18nKey="lib:profilePicture.photoTypesAllowed"
                    values={{
                      val: filterAllowedFileFormats(allowedFileFormatTypes),
                    }}
                    components={[<Typography component="span" typography="header3" />]}
                  />
                </Typography>

                <Typography color="gray.56" typography="large">
                  <Trans
                    i18nKey="lib:profilePicture.maxSize"
                    values={{ size: MAX_IMAGE_SIZE_MB }}
                    components={[<Typography component="span" typography="header3" />]}
                  />
                </Typography>
              </Stack>
            </form>
          </FormProvider>
        </DialogContent>
      </DialogLayout>
    )
  }
)
ModalUploadPhoto.propTypes = {
  hasCam: PropTypes.bool,
  allowedTypes: PropTypes.array,
  directOpen: PropTypes.bool,
  clientId: PropTypes.string,
  imageDelayedToLoad: PropTypes.bool,
  isBannerImage: PropTypes.bool,
  maxImageSize: PropTypes.shape({
    width: PropTypes.number,
    height: PropTypes.number,
  }),
  usePopDialog: PropTypes.bool,
  cropTitle: PropTypes.string,
  mutation: PropTypes.shape({
    mutate: PropTypes.func.isRequired,
    isLoading: PropTypes.bool.isRequired,
    responseKey: PropTypes.string.isRequired,
    onUploadSuccess: PropTypes.func,
    additionalData: PropTypes.object,
  }).isRequired,
}
ModalUploadPhoto.defaultProps = {
  hasCam: true,
  clientId: '',
  isBannerImage: false,
}
export default ModalUploadPhoto
